/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	Text,
	TextField,
	Button,
	Icon,
	Pressable,
	Radiobutton,
	Image,
} from '@components/atoms'
import { Gallery, Modal, ModalInfoType } from '@components/molecules'
import { ReactImageGalleryItem } from 'react-image-gallery'
import { LinearProgress } from '@mui/material'

import { useAppSelector, CartType, ApiGetDataStatus } from '@redux/index'
import { useNavigate } from 'react-router-dom'
import { SeasonProductType, ProductOption, ProductOptionType } from '@schemas'
import { grey, pink, deepPurple, lime, cyan, yellow } from '@mui/material/colors'
import dayjs from 'dayjs'
import { Decimal } from '@utils'

interface PropsType {
	title: string
	subtitle: string
	images: ReactImageGalleryItem[]
	qty: number
	onQtyChange: (qty: number) => void
	product: SeasonProductType
}

import eventImg from '@public/images/pages/event/event2.jpg'

export const ShopOutline = (props: PropsType) => {
	// hook
	const navigate = useNavigate()

	const pageStatus: ApiGetDataStatus = useAppSelector(state => state.shop.shopItemStatus)
	const optionList: ProductOptionType[] = useAppSelector(state => state.shop.optionList)

	const defaultOption: ProductOptionType =
		optionList.length > 0 ? optionList[0] : new ProductOption()

	// state
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [modalInfo, setModalInfo] = React.useState<ModalInfoType>({
		target: '',
		title: '',
	})
	const [selectedOption, setSelectedOption] =
		React.useState<ProductOptionType>(defaultOption)

	const stockStatus: { status: string; label: string } = React.useMemo(() => {
		const status =
			props.product.status === 'active' && props.product.preorderYn === 1
				? 'preorder'
				: props.product.status === 'active'
				? 'inStock'
				: 'soldout'
		const label =
			props.product.status === 'active' && props.product.preorderYn === 1
				? 'Pre-Order'
				: props.product.status === 'active'
				? 'In Stock'
				: 'Sold Out'

		return {
			status: status,
			label: label,
		}
	}, [props.product])

	const isDiscounted = props.product.seasonUsd !== props.product.standardUsd
	const subtotalUsd = React.useMemo(() => {
		const res = Decimal(props.product.seasonUsd * props.qty)
		return res
	}, [props.qty])

	const priceExUsd: number = React.useMemo(() => Decimal(subtotalUsd), [subtotalUsd])

	const vatUsd: number = React.useMemo(
		() => Decimal(subtotalUsd - priceExUsd),
		[subtotalUsd, priceExUsd],
	)

	const localQty: number = React.useMemo(() => {
		const localeCartSrt = localStorage.getItem('cart')
		const tempItemList: CartType[] =
			localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)
		const localCartList = tempItemList.map(anItem => {
			const vatSumUsd = Decimal(anItem.sumUsd * anItem.vatUsd)
			return {
				...anItem,
				vatSumUsd: vatSumUsd,
			}
		})
		const idx = localCartList.findIndex(anItem => anItem.id === props.product.id)
		const res: number = idx === -1 ? 0 : localCartList[idx].qty

		return res
	}, [])

	const status: string = React.useMemo(() => {
		return props.product.seasonQuantity - props.product.soldQuantity - localQty === 0
			? 'sold_out'
			: props.product.status
	}, [props.product, localQty])

	const onQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const localeCartSrt = localStorage.getItem('cart')
		const tempItemList: CartType[] =
			localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)
		const localCartList = tempItemList.map(anItem => {
			const vatSumUsd = anItem.sumUsd * anItem.vatUsd
			return {
				...anItem,
				vatSumUsd: vatSumUsd,
			}
		})
		const idx = localCartList.findIndex(anItem => anItem.id === props.product.id)
		const localQty: number = idx === -1 ? 0 : localCartList[idx].qty

		const selectedQty =
			Number(str) > props.product.seasonQuantity - props.product.soldQuantity - localQty
				? props.product.seasonQuantity - props.product.soldQuantity - localQty
				: Number(str)

		if (typeof props.onQtyChange === 'function') {
			props.onQtyChange(selectedQty)
		}
	}

	const onAddToCartClick = () => {
		const newCartItem: CartType = {
			id: props.product.id,
			seasonId: props.product.seasonId,
			productId: props.product.productId,
			productCode: props.product.productCode,
			seasonUsd: props.product.seasonUsd,
			seasonDiscountUsd: props.product.seasonDiscountUsd,
			productName: props.product.productName,
			standardUsd: props.product.standardUsd,
			seasonQuantity: props.product.seasonQuantity,
			soldQuantity: props.product.soldQuantity,
			qty: props.qty,
			sumUsd: priceExUsd,
			vatUsd: vatUsd,
		}

		if (selectedOption.id !== '') {
			newCartItem.option = { ...selectedOption }
		}

		const localeCartList = localStorage.getItem('cart')
		const tempCartList =
			localeCartList === null || localeCartList === '' ? [] : JSON.parse(localeCartList)
		const newCartList = (() => {
			if (Array.isArray(tempCartList)) {
				if (tempCartList.length === 0) {
					return [newCartItem]
				}

				const idx = tempCartList.findIndex(anItem => anItem.id === newCartItem.id)

				if (idx === -1) {
					return [...tempCartList, newCartItem]
				} else {
					const qty = ((tempCartList[idx].qty ?? 0) as number) + newCartItem.qty
					tempCartList[idx].qty = qty
					tempCartList[idx].sumUsd = Decimal(tempCartList[idx].seasonUsd * qty)

					return tempCartList
				}
			} else {
				return [newCartItem]
			}
		})()

		localStorage.setItem('cart', JSON.stringify(newCartList))
		window.dispatchEvent(new Event('storage'))

		setModalInfo({
			target: 'addToCart',
			title: 'Add to Cart',
		})
		setShowModal(true)
	}

	const onModalSubmit = () => {
		if (modalInfo.target === 'addToCart') {
			navigate('/cart')
		}
	}

	const onOptionRadioChange = (anOption: ProductOptionType) => {
		setSelectedOption(anOption)
	}

	return (
		<View css={style}>
			{/* <View className="eventView">
				<Image src={eventImg} className="eventView" />
			</View> */}
			<View className="shopTitleView">
				<View className="backButtonView">
					<Pressable className="backButton" onClick={() => navigate('/shop')}>
						<Icon name="arrow-left" className="backIcon" />
						<Text className="backTitle">Back to Shop</Text>
					</Pressable>
				</View>

				{/* 제품명은 페이지의 h1. 검색엔진이 이 페이지가 무엇에 대한 것인지 읽는 신호다. */}
				<Text as="h1" category="h1" className="shopTitle">
					{props.title}
				</Text>
				<Text as="h2" category="h1" className="shopSubtitle">
					{props.subtitle}
				</Text>
			</View>
			<View className="shopOutlinView">
				<View className="shopGalleryView">
					<Gallery items={props.images} />
				</View>

				<View className="shopOutlineContentView">
					<View className={`stockLabel ${stockStatus.status}`}>
						<Text category="h2">{stockStatus.label}</Text>
					</View>

					<Text category="label" hint="500" className="unitPriceLabel">
						Unit Price
					</Text>

					{isDiscounted && (
						<View className="canceledPriceView">
							<Text category="p1" className="standardPrice" hint="700">
								{props.product.standardUsd.toLocaleString('en-US', {
									style: 'currency',
									currency: 'USD',
								})}
							</Text>
						</View>
					)}
					<View className="unitPriceInfoView">
						<Text category="h5" className="seasonPrice">
							{props.product.seasonUsd.toLocaleString('en-US', {
								style: 'currency',
								currency: 'USD',
							})}
						</Text>
					</View>

					{['init', 'loading'].includes(pageStatus) ? (
						<View className="loadingView">
							<LinearProgress className="loadingProgress" />
						</View>
					) : (
						<>
							{status === 'active' && (
								<View className="qtyInfoView">
									<Text category="label" hint="500" className="shopLabel">
										Quantity
									</Text>

									<TextField
										className="qtyTextField"
										type="number"
										size="medium"
										InputProps={{ inputProps: { min: 1 } }}
										value={props.qty}
										onChange={onQtyChange}
									/>
								</View>
							)}

							{status === 'active' && (
								<View className="totalPriceView">
									<Text category="label" hint="500" className="shopLabel">
										Total Price
									</Text>

									<Text category="h1" className="totalPrice">
										{(priceExUsd + vatUsd).toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}
									</Text>
								</View>
							)}

							{optionList.length > 0 && (
								<View className="optionView">
									<Text className="optionTitle">Options</Text>
									{optionList.map(anOption => {
										return (
											<View key={anOption.id} className="optionButtonView">
												<Pressable
													className="optionButton"
													onClick={() => onOptionRadioChange(anOption)}
												>
													<Radiobutton
														value={anOption.id}
														onRadioChange={() => onOptionRadioChange(anOption)}
														selectedValue={selectedOption.id}
													/>
													<Text>{anOption.name}</Text>
												</Pressable>
											</View>
										)
									})}
								</View>
							)}

							<View className="addToCartView">
								{status === 'active' ? (
									<>
										<Button
											className="addToCartButton"
											variant="contained"
											size="large"
											onClick={onAddToCartClick}
										>
											Add to Cart
										</Button>
									</>
								) : status === 'not_start' ? (
									<>
										<Text category="h1" className="commingSoon">
											Pre-Order Starts
										</Text>

										<Text category="h3" className="commingSoonDate">
											{`on ${dayjs(props.product.seasonStartDate).format(
												'MMM DD, YYYY',
											)}`}
										</Text>
									</>
								) : (
									<>
										<Button
											className="addToCartButton disabled"
											variant="contained"
											size="large"
											disabled
										>
											Add to Cart
										</Button>
									</>
									// <Text category="h1" className="soldout">
									// 	SOLD OUT
									// </Text>
								)}

								{stockStatus.status === 'preorder' && (
									<View className="preorederAlertView">
										<View className="preorderAlertTitleView">
											<Icon name="message-alert-outline" className="alertIcon" />
											<Text category="h5">This is a Pre-Order.</Text>
										</View>
										<Text category="p1">
											This item will ship later. It is a pre-order.
										</Text>

										{/* <Divider />

										<View className="eventView">
											<Text className="eventTitle" category="h4">
												📢 Pre-Order Closing Soon!
											</Text>
											<ul className="eventUl">
												<li className="eventLi">⏰ Finish in May</li>
												<li className="eventLi">🚀 Shipping starts in July</li>
												<li className="eventLi">🚛 Free shipping – no extra cost!</li>
											</ul>

											<Text className="eventPrice" category="h4">
												💥 Special Prices:
											</Text>
											<Text className="eventPriceUnitLabel" category="s1">
												Kedem 6:
											</Text>
											<View className="eventPriceUnitView">
												<Text className="eventNormalPrice">$407</Text>
												<Text className="eventPriceUnitArrow">⮕</Text>
												<Text className="eventDiscountedPrice">$369</Text>
											</View>
											<Text className="eventPriceUnitLabel" category="s1">
												Kedem 4:
											</Text>
											<View className="eventPriceUnitView">
												<Text className="eventNormalPrice">$341</Text>
												<Text className="eventPriceUnitArrow">⮕</Text>
												<Text className="eventDiscountedPrice">$305</Text>
											</View>
										</View> */}
									</View>
								)}
							</View>
						</>
					)}
				</View>
			</View>
			<Modal
				target={modalInfo.target}
				title={modalInfo.title}
				show={showModal}
				closeStatus="inherit"
				showSubmit={true}
				closeTitle="Stay Here"
				submitTitle="Move to Cart"
				submitIconColor={deepPurple['A400']}
				onSubmit={onModalSubmit}
			>
				{modalInfo.target ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">The product has been added to your cart.</Text>
						<Text category="p2">Would you like to move to the cart?</Text>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

const style = css({
	'.eventView': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',

		'.eventImage': {
			width: '100%',
			height: 'auto',
			margin: 'auto',
			left: 0,
			right: 0,
			bottom: 0,
		},
	},
	'.shopTitleView': {
		textAlign: 'left',

		'.backButtonView': {
			marginTop: '3rem',
			'.backButton': {
				display: 'flex',
				alignItems: 'center',
				width: '13rem',

				'&:hover': {
					background: grey[200],
				},

				'.backIcon': {
					marginRight: '0.5rem',
					fontSize: '20px',
				},
				'.backTitle': {
					fontSize: '20px',
				},
			},
		},

		'.shopTitle': {
			marginTop: '2rem',
			fontSize: '80px',
			lineHeight: '60px',
		},
		'.shopSubtitle': {
			marginTop: '-2rem',
			marginBottom: '4rem',
		},
	},

	'.shopOutlinView': {
		position: 'relative',
		display: 'flex',
		height: '40rem',

		'.shopGalleryView': {
			minWidth: 'calc(100% - 35rem)',
		},
		'.shopOutlineContentView': {
			width: '35rem',
			paddingLeft: '2rem',
			paddingRight: '2rem',
			textAlign: 'center',

			'.canceledPriceView': {
				textDecoration: 'line-through',
			},

			'.stockLabel': {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				width: '100%',
				height: '5rem',
				margin: 'auto',
				marginBottom: '3rem',
				background: grey[500],
				color: 'white',
				borderRadius: '5px',

				'&.inStock': {
					marginBottom: '1rem',
					background: yellow['A400'],
					color: grey[900],
				},

				'&.preorder': {
					marginBottom: '1rem',
					background: cyan['A400'],
					color: grey[900],
				},
			},

			'.unitPriceLabel': {
				margin: 0,
				textDecoration: 'underline',
			},

			'.unitPriceInfoView': {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				marginBottom: '4rem',

				'.standardPriceView': {
					marginRight: '0.5rem',

					'.standardPrice': {
						textDecoration: 'line-through',
					},
				},
				'.seasonPrice': {
					marginTop: 0,
					marginBottom: 0,
				},
			},

			'.shopLabel': {
				marginTop: '2rem',
				textDecoration: 'underline',
			},

			'.qtyInfoView': {
				marginBottom: '4rem',

				'.qtyTextField': {
					width: '8rem',
				},
			},

			'.totalPriceView': {
				marginTop: '2rem',

				'.totalPrice': {
					margin: 0,
				},
			},

			'.optionView': {
				marginTop: '1rem',
				paddingBottom: '1rem',
				border: '1px solid',
				borderColor: grey[400],
				borderRadius: '10px',
				'.optionTitle': {
					paddingBottom: '0.8rem',
					paddingLeft: '0.5rem',
					textAlign: 'left',
					borderBottom: '1px solid',
					borderColor: grey[400],
				},
				'.optionButton': {
					display: 'flex',
				},
			},

			'.addToCartView': {
				marginTop: '3rem',

				'.addToCartButton': {
					width: '12rem',
					height: '4rem',
					background: grey[900],

					'&.disabled': {
						background: grey[300],
						color: grey[500],
					},
				},

				'.soldout': {
					color: pink['A400'],
				},
				'.commingSoon': {
					marginBottom: 0,
					color: pink['A400'],
				},
				'.commingSoonDate': {
					marginTop: '0.5rem',
					color: pink['A400'],
				},

				'.preorederAlertView': {
					marginTop: '2rem',
					paddingBottom: '1rem',
					background: lime['A400'],
					borderRadius: '10px',

					'.preorderAlertTitleView': {
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',

						'.alertIcon': {
							marginRight: '0.5rem',
							color: grey[900],
						},
					},

					'.eventView': {
						width: '100%',
						padding: '1rem',
						background: lime['A200'],
						borderRadius: '0 0 10px 10px',
						color: grey[900],
						textAlign: 'left',

						'.eventTitle': {
							marginTop: '0',
							marginBottom: '0',
						},

						'.eventUl': {
							'.eventLi': {
								fontSize: '14px',
								fontWeight: '800',
								marginLeft: '1rem',
								marginBottom: '0.5rem',
							},
						},

						'.eventPrice': {
							marginTop: '2rem',
							marginBottom: '1rem',
						},

						'.eventPriceUnitLabel': {
							marginLeft: '3rem',
							textDecoration: 'underline',
						},

						'.eventPriceUnitView': {
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',

							'.eventNormalPrice': {
								margin: 0,
								marginLeft: '1rem',
								marginRight: '1rem',
								textDecoration: 'line-through',
								fontSize: '15px',
								fontWeight: '300',
							},
							'.eventPriceUnitArrow': {
								margin: 0,
							},
							'.eventDiscountedPrice': {
								margin: 0,
								marginLeft: '1rem',
								fontSize: '30px',
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 1350px)': {
		'.shopOutlinView': {
			height: 'fit-content',
			'.shopGalleryView': {
				width: 'calc(100% - 20rem)',
				minWidth: 'calc(100% - 20rem)',
			},
			'.shopOutlineContentView': {
				width: '32%',
			},
		},
	},

	'@media (max-width: 900px)': {
		'.shopOutlinView': {
			display: 'block',
			'.shopGalleryView': {
				width: '100%',
			},
			'.shopOutlineContentView': {
				width: '100%',
				paddingTop: '3rem',

				'.unitPriceInfoView': {
					marginBottom: '1rem',
				},

				'.qtyInfoView': {
					marginBottom: '1rem',
				},
			},
		},
	},

	'@media (max-width: 500px)': {
		'.shopTitleView': {
			'.shopTitle': {
				fontSize: '60px',
			},
		},

		'.shopOutlinView': {
			'.shopOutlineContentView': {
				paddingLeft: '1rem',
				paddingRight: '1rem',

				'.addToCartView': {
					'.preorederAlertView': {
						'.eventView': {
							padding: '1rem',

							'.eventTitle': {
								marginTop: '0',
								marginBottom: '0',
							},

							'.eventUl': {
								'.eventLi': {
									fontSize: '14px',
									marginLeft: '1rem',
									marginBottom: '0.5rem',
								},
							},

							'.eventPriceUnitView': {
								'.eventDiscountedPrice': {
									fontSize: '24px',
								},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 400px)': {
		'.shopTitleView': {
			'.shopTitle': {
				fontSize: '50px',
			},

			'.shopSubtitle': {
				fontSize: '24px',
			},
		},

		'.shopOutlinView': {
			'.shopOutlineContentView': {
				paddingLeft: '0',
				paddingRight: '0',

				'.addToCartView': {
					'.preorederAlertView': {
						'.eventView': {
							padding: '1rem 0.5rem',

							'.eventTitle': {
								fontSize: '20px',
								marginTop: '0',
								marginBottom: '0',
							},

							'.eventUl': {
								'.eventLi': {
									fontSize: '14px',
									marginLeft: '0',
									marginBottom: '0.5rem',
								},
							},

							'.eventPrice': {
								fontSize: '20px',
								marginTop: '2rem',
								marginBottom: '1rem',
							},

							'.eventPriceUnitLabel': {
								marginLeft: '2rem',
								textDecoration: 'underline',
							},

							'.eventPriceUnitView': {
								'.eventNormalPrice': {
									margin: 0,
									marginLeft: '1rem',
									marginRight: '1rem',
									fontSize: '15px',
									fontWeight: '300',
								},
								'.eventPriceUnitArrow': {
									margin: 0,
								},
								'.eventDiscountedPrice': {
									margin: 0,
									marginLeft: '1rem',
									fontSize: '20px',
								},
							},
						},
					},
				},
			},
		},
	},
})
